import { Maybe } from 'maybeasy';
import { RationaleItem } from '../../DashboardSummaryStore/Types';

/**
 * Remove unnecessary .0 decimals from whole numbers in text
 * e.g., "3.5/5.0" becomes "3.5/5", "10.0%" becomes "10%"
 * Only removes .0 when it's a whole number (keeps 3.5, removes 5.0)
 */
export const cleanDecimalFormatting = (text: string): string => {
  // Match numbers ending in .0 that are followed by non-digit or end of string
  // This handles cases like "5.0/", "5.0%", "5.0 ", "5.0)" etc.
  return text.replace(/(\d+)\.0(?=\D|$)/g, '$1');
};

// Storage key for persisting AI summary date filters
const AI_SUMMARY_DATE_FILTER_KEY = 'ai-summary-date-filter';

interface StoredDateFilter {
  startDate: string;
  endDate: string;
  timestamp: number;
}

/**
 * Save date filter to localStorage for persistence across pages
 */
export const saveDateFilter = (startDate: string, endDate: string): void => {
  try {
    const data: StoredDateFilter = {
      startDate,
      endDate,
      timestamp: Date.now(),
    };
    localStorage.setItem(AI_SUMMARY_DATE_FILTER_KEY, JSON.stringify(data));
  } catch {
    // Silently fail if localStorage is not available
  }
};

/**
 * Load date filter from localStorage
 * Returns null if not found or expired (older than 24 hours)
 */
export const loadDateFilter = (): { startDate: string; endDate: string } | null => {
  try {
    const stored = localStorage.getItem(AI_SUMMARY_DATE_FILTER_KEY);
    if (!stored) return null;

    const data: StoredDateFilter = JSON.parse(stored);

    // Check if filter is older than 24 hours
    const maxAge = 24 * 60 * 60 * 1000; // 24 hours in ms
    if (Date.now() - data.timestamp > maxAge) {
      localStorage.removeItem(AI_SUMMARY_DATE_FILTER_KEY);
      return null;
    }

    return {
      startDate: data.startDate,
      endDate: data.endDate,
    };
  } catch {
    return null;
  }
};

// Get default date range (1 year ago to today)
export const getDefaultDateRange = (): { startDate: string; endDate: string } => {
  const today = new Date();
  const oneYearAgo = new Date();
  oneYearAgo.setFullYear(today.getFullYear() - 1);

  const formatDate = (date: Date): string => date.toISOString().split('T')[0];

  return {
    startDate: formatDate(oneYearAgo),
    endDate: formatDate(today),
  };
};

export const formatDateRange = (startDate?: string, endDate?: string): string => {
  if (!startDate || !endDate) return '';

  const formatDate = (dateStr: string): string => {
    try {
      // Parse as local date to avoid timezone issues
      // Input format: "YYYY-MM-DD"
      const [year, month, day] = dateStr.split('-').map(Number);
      const date = new Date(year, month - 1, day); // month is 0-indexed
      return date.toLocaleDateString('en-US', {
        month: 'short',
        day: 'numeric',
        year: 'numeric',
      });
    } catch {
      return dateStr;
    }
  };

  return `${formatDate(startDate)} - ${formatDate(endDate)}`;
};

export const formatGeneratedTime = (isoString: string): string => {
  try {
    const date = new Date(isoString);
    return `Generated ${date.toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
    })} at ${date.toLocaleTimeString('en-US', {
      hour: 'numeric',
      minute: '2-digit',
      hour12: true,
    })}`;
  } catch {
    return '';
  }
};

/**
 * Convert markdown to HTML for rich text clipboard
 * Handles: bold (**text**), bullets (- or *), numbered lists, headers (##)
 */
const markdownToHtml = (markdown: string): string => {
  let html = markdown;

  // Convert headers (## Header) to bold
  html = html.replace(/^##\s*(.+)$/gm, '<strong>$1</strong>');

  // Convert bold (**text**) to <strong>
  html = html.replace(/\*\*(.+?)\*\*/g, '<strong>$1</strong>');

  // Convert bullet points to list items
  // First, identify bullet list blocks and wrap them
  const lines = html.split('\n');
  const processedLines: string[] = [];
  let inBulletList = false;

  for (const line of lines) {
    const trimmed = line.trim();
    const isBullet = /^[-*]\s+/.test(trimmed);

    if (isBullet) {
      if (!inBulletList) {
        processedLines.push('<ul>');
        inBulletList = true;
      }
      // Convert bullet to list item
      const content = trimmed.replace(/^[-*]\s+/, '');
      processedLines.push(`<li>${content}</li>`);
    } else {
      if (inBulletList) {
        processedLines.push('</ul>');
        inBulletList = false;
      }
      if (trimmed) {
        processedLines.push(`<p>${trimmed}</p>`);
      }
    }
  }

  if (inBulletList) {
    processedLines.push('</ul>');
  }

  return processedLines.join('');
};

/**
 * Build text content for copying summary to clipboard (plain text version)
 * @param headline - Optional headline text
 * @param summaryText - Main summary text
 * @param rationale - Optional rationale items
 * @returns Formatted plain text suitable for clipboard
 */
const buildPlainText = (
  headline: Maybe<string>,
  summaryText: string,
  rationale: Maybe<RationaleItem[]>,
): string => {
  const parts: string[] = [];

  headline.cata({
    Just: (h) => parts.push(h),
    Nothing: () => {},
  });

  // Keep bullets but strip markdown bold markers for plain text
  const plainSummary = summaryText.replace(/\*\*/g, '');
  parts.push(plainSummary);

  rationale.cata({
    Just: (items) => {
      if (items.length > 0) {
        parts.push('\n--- AI Rationale ---');
        items.forEach((item) => {
          parts.push(`\n"${item.quote}"\nSource: ${item.source}`);
        });
      }
    },
    Nothing: () => {},
  });

  return parts.join('\n\n');
};

/**
 * Build HTML content for copying summary to clipboard (rich text version)
 * Preserves formatting like bold, bullets, etc.
 */
const buildHtmlText = (
  headline: Maybe<string>,
  summaryText: string,
  rationale: Maybe<RationaleItem[]>,
): string => {
  const parts: string[] = [];

  headline.cata({
    Just: (h) => parts.push(`<p><strong style="color: #16a34a;">${h}</strong></p>`),
    Nothing: () => {},
  });

  parts.push(markdownToHtml(summaryText));

  rationale.cata({
    Just: (items) => {
      if (items.length > 0) {
        parts.push('<hr><p><strong>AI Rationale</strong></p>');
        items.forEach((item) => {
          parts.push(`<blockquote><em>"${item.quote}"</em></blockquote>`);
          parts.push(`<p><small>Source: ${item.source}</small></p>`);
        });
      }
    },
    Nothing: () => {},
  });

  return parts.join('');
};

/**
 * Copy summary content to clipboard with both plain text and HTML formats
 * Rich text editors will get formatted content, plain text editors get plain text
 * @param headline - Optional headline text
 * @param summaryText - Main summary text
 * @param rationale - Optional rationale items
 * @returns Promise that resolves to true if copy succeeded, false otherwise
 */
export const copySummaryToClipboard = async (
  headline: Maybe<string>,
  summaryText: string,
  rationale: Maybe<RationaleItem[]>,
): Promise<boolean> => {
  try {
    const plainText = buildPlainText(headline, summaryText, rationale);
    const htmlText = buildHtmlText(headline, summaryText, rationale);

    // Try to use ClipboardItem API for rich text support
    if (typeof ClipboardItem !== 'undefined') {
      const clipboardItem = new ClipboardItem({
        'text/plain': new Blob([plainText], { type: 'text/plain' }),
        'text/html': new Blob([htmlText], { type: 'text/html' }),
      });
      await navigator.clipboard.write([clipboardItem]);
    } else {
      // Fallback to plain text only
      await navigator.clipboard.writeText(plainText);
    }
    return true;
  } catch {
    // Fallback to plain text on error
    try {
      const plainText = buildPlainText(headline, summaryText, rationale);
      await navigator.clipboard.writeText(plainText);
      return true;
    } catch {
      console.error('Failed to copy to clipboard');
      return false;
    }
  }
};
